import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import { useSession } from "users/__session/session-context";
import { User } from "users/user";
import { useCreateFeeScheduleTemplateMutation } from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";
import useCreateTemplateAction, {
  type CreateTemplateAction,
} from "work/proposals/_views/_shared/use-create-template-action";

type FeeScheduleTemplateCategory = FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;

/** A template the server has made, which therefore has an id. */
export type CreatedFeeScheduleTemplate = FeeScheduleTemplate & { readonly id: Guid };

export type CreateFeeScheduleTemplateActionOptions = {
  /** The fee schedule the proposal holds as the user sees it, which is what the template saves. */
  categories: readonly FeeScheduleTemplateCategory[];
  /** The proposal's name, which the template's default name is made from. */
  proposalName?: string | null;
  /** Called with the template once the server has made it. */
  onTemplateCreated?: (template: CreatedFeeScheduleTemplate) => void;
};

/**
 * Save as Template for a proposal's Fee Schedule tab: saves the proposal's fee schedule as a new fee
 * schedule template. The draft and active pages both use it (#5394).
 *
 * The categories' staffing travels with them (#5237): the people they name are on this proposal's
 * vendor team, and a template that remembers them is the point of the feature.
 */
export default function useCreateFeeScheduleTemplateAction(
  options: Readonly<CreateFeeScheduleTemplateActionOptions>,
): CreateTemplateAction {
  const { categories, proposalName, onTemplateCreated } = options;
  const session = useSession();
  const [createFeeScheduleTemplate] = useCreateFeeScheduleTemplateMutation();

  async function handleCreate(name: string): Promise<boolean> {
    const created = await saveFeeScheduleTemplate(createFeeScheduleTemplate, {
      name,
      categories,
      creator: session.user,
    });
    if (!created) return false;
    onTemplateCreated?.(created);
    return true;
  }

  return useCreateTemplateAction({
    dialogTitle: "Save as Fee Schedule Template",
    disabledReason: categories.length ? undefined : "Add at least one category before saving a template",
    defaultName: getDefaultFeeScheduleTemplateName(proposalName),
    onCreate: handleCreate,
  });
}

export function getDefaultFeeScheduleTemplateName(proposalName?: string | null): string {
  const trimmedName = proposalName?.trim();
  return trimmedName ? `${trimmedName} Fee Schedule` : "Proposal Fee Schedule";
}

/**
 * Sends one create and reports the template it made, or undefined once the user has been told why
 * there is none.
 *
 * At module scope so the hook above stays compilable by the React Compiler. See app/CLAUDE.md.
 */
async function saveFeeScheduleTemplate(
  createFeeScheduleTemplate: ReturnType<typeof useCreateFeeScheduleTemplateMutation>[0],
  request: { name: string; categories: readonly FeeScheduleTemplateCategory[]; creator: User | null },
): Promise<CreatedFeeScheduleTemplate | undefined> {
  if (!request.categories.length || !request.creator) return undefined;
  try {
    const response = await createFeeScheduleTemplate({
      template: new FeeScheduleTemplate({
        name: request.name,
        tags: [],
        categories: [...request.categories],
        creator: request.creator.toEntityMember(),
      }),
      // A proposal's fee schedule is a list of snapshots, not catalog references. Some arrive with a
      // client-minted id (typed in via the add panel) and some retain the id of the template
      // category they were copied from, but even the latter are snapshots: their fee is negotiated
      // per proposal and may already have diverged from the catalog row. Nothing on the objects
      // distinguishes the two, so the whole array is declared as values and the server promotes
      // each one into the catalog by normalized name.
      categorySource: "values",
    }).unwrap();
    const createdTemplate = FeeScheduleTemplate.fromJSON(response);
    if (!hasId(createdTemplate)) throw new Error("Created fee schedule template is missing an ID");
    enqueueSnackbar("Fee schedule template created", { variant: "success" });
    return createdTemplate;
  } catch (error) {
    console.error("Failed to create fee schedule template from proposal", error);
    showErrorSnackbar(error, { doing: "creating the fee schedule template", operation: "proposal.create-fee-schedule-template" });
    return undefined;
  }
}

function hasId(template: FeeScheduleTemplate): template is CreatedFeeScheduleTemplate {
  return Boolean(template.id);
}
